import React, { useEffect, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import * as Yup from 'yup';

import { Field } from '~/app/shared/components/Form';
import { SelectableAccount } from '~/types';
import { SmartPayCustomerType } from '~/app/pages/accounting/bank/smartpay/types';
import SignupForm from '../../SignupForm';
import Select2FormField from './CompanyInfoDropdown';
import AddBankAccountDialogButton from './AddBankAccountDialogButton';
import EditCompanyInfoDialogButton from './EditCompanyInfoDialogButton';
import { useSignupDialogOnboardingContext } from '../../../contexts/SignupDialogOnboardingContextProvider';
import { getCompleteAccountNumber, isValidBankOrFeeAccount, mapFeeAccountsByAccountNumber } from '../../../utils';
import {
    COMPANY_INFORMATION_STEP,
    COMPANY_INFORMATION_STEP_MIXPANEL,
    trackSmartPayOnboardingMixpanel,
    trackSmartPayOnboarding,
} from '../../../../../tracking/tracking';

const CompanyInformation = () => {
    const { bankAccounts, feeAccounts, journals, NFCustomerData } = useSignupDialogOnboardingContext();

    const customerType = NFCustomerData?.customerType ?? SmartPayCustomerType.NewCustomer;
    const novemberFirstCustomerNumberList = NFCustomerData?.novemberFirstCustomerNumberList ?? [];

    useEffect(() => {
        trackSmartPayOnboardingMixpanel(COMPANY_INFORMATION_STEP_MIXPANEL);
        trackSmartPayOnboarding(COMPANY_INFORMATION_STEP);
    }, []);

    const bankAccountsMap = useMemo<Map<number, SelectableAccount>>(
        () => mapFeeAccountsByAccountNumber(feeAccounts),
        [feeAccounts]
    );

    const bankAccountOptions = bankAccounts.map(account => ({
        label: `${account.number} ${account.bankName} - ${getCompleteAccountNumber(
            account.bankRegistrationNumber,
            account.bankAccountNumber
        )}`,
        value: account.number,
    }));

    const journalOptions = journals.map(journal => ({
        label: journal.name,
        value: journal.id,
    }));
    // add default option for adding new journal
    journalOptions.push({ label: t`Smart Pay (New journal is created automatically)`, value: 0 });

    const feeAccountOptions = feeAccounts.map(account => ({
        label: `${account.number} ${account.name}`,
        value: account.number,
    }));

    const nfCustomerNumberOptions = novemberFirstCustomerNumberList.map(nfCustomerNumber => ({
        label: nfCustomerNumber,
        value: nfCustomerNumber,
    }));

    const validationSchema = () => {
        let obj = {
            cvr: Yup.string().required(t`CVR is required.`),
            nfCustomerNumber: Yup.string(),
            bankAccount: Yup.number().notRequired().nullable(),
            journalNumber: Yup.number().notRequired().nullable(),
            feeJournalNumber: Yup.number().notRequired().nullable(),
        };

        if (customerType !== SmartPayCustomerType.NewCustomer) {
            obj = { ...obj, nfCustomerNumber: Yup.string().required() };
        }

        if (customerType !== SmartPayCustomerType.ExistingCustomerWithIntegration) {
            obj = {
                ...obj,
                bankAccount: Yup.number()
                    .required()
                    .test(
                        'bank-account-valid',
                        t`Only accounts of type Profit and loss or Balance can be used in Smart Pay.`,
                        value => isValidBankOrFeeAccount(value, bankAccountsMap)
                    ),
                journalNumber: Yup.number().required(),
                feeJournalNumber: Yup.number()
                    .required()
                    .test(
                        'fee-account-valid',
                        t`Only accounts of type Profit and loss or Balance can be used in Smart Pay.`,
                        value => isValidBankOrFeeAccount(value, bankAccountsMap)
                    ),
            };
        }

        return Yup.object(obj);
    };

    return (
        <SignupForm validationSchema={validationSchema}>
            <Field.Text name="cvr" label={t`CVR number`} required readOnly />

            <Field.Text name="company" label={t`Company name`} required readOnly />

            <div className="flex justify-end">
                <EditCompanyInfoDialogButton />
            </div>

            {customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration ? (
                <div className="mt-6 mb-2">
                    <Trans>
                        In order to migrate your existing November first account to Smart Pay, please insert your November First
                        account number and select settings for your supplier payments.
                    </Trans>
                </div>
            ) : customerType === SmartPayCustomerType.ExistingCustomerWithIntegration ? (
                <div className="mt-6 mb-2">
                    <Trans>
                        In order to migrate your existing November first account to Smart Pay, please select your November First
                        customer number.
                    </Trans>
                </div>
            ) : null}

            {customerType === SmartPayCustomerType.ExistingCustomerWithIntegration ? (
                <Select2FormField
                    name="nfCustomerNumber"
                    label={t`November First customer number`}
                    options={nfCustomerNumberOptions}
                    required
                />
            ) : customerType === SmartPayCustomerType.ExistingCustomerWithoutIntegration ? (
                <Field.Text name="nfCustomerNumber" label={t`November First customer number`} required />
            ) : null}

            {customerType !== SmartPayCustomerType.ExistingCustomerWithIntegration && (
                <>
                    <Select2FormField
                        name="bankAccount"
                        label={t`Bank account for supplier payments (More can be added later)`}
                        options={bankAccountOptions}
                        required
                    />

                    <div className="flex justify-end">
                        <AddBankAccountDialogButton />
                    </div>

                    <Select2FormField
                        name="journalNumber"
                        label={t`Journal for Smart Pay payments (Can be changed later)`}
                        options={journalOptions}
                        required
                    />

                    <Select2FormField
                        name="feeJournalNumber"
                        label={t`Account for payment fee (Can be changed later)`}
                        options={feeAccountOptions}
                        required
                    />
                </>
            )}
        </SignupForm>
    );
};

export default CompanyInformation;
